import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  GraduationCap,
  Plus,
  Search,
  Video,
  FileText,
  Link as LinkIcon,
  Users,
  Clock,
  CheckCircle2,
  AlertCircle,
  Trash2,
  Pencil,
  Send,
  Play,
  Upload,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  BarChart3,
  BookOpen,
  Award,
  Target,
  TrendingUp,
  Loader2,
  Filter,
  Presentation,
  HelpCircle,
  Eye,
  X,
  RotateCcw,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Separator } from "../components/ui/separator";
import { Progress } from "../components/ui/progress";
import { QuizBuilder, QuizTaker, CertificateViewer } from "../components/QuizComponents";
import CategoryManager from "../components/CategoryManager";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MATERIAL_ICONS = {
  video: Video,
  document: FileText,
  link: LinkIcon,
  quiz: HelpCircle,
  presentation: Presentation,
};

const CATEGORY_COLORS = {
  safety: "bg-red-500/10 text-red-600 border-red-500/30",
  onboarding: "bg-blue-500/10 text-blue-600 border-blue-500/30",
  compliance: "bg-amber-500/10 text-amber-600 border-amber-500/30",
  skills: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
  product: "bg-purple-500/10 text-purple-600 border-purple-500/30",
  leadership: "bg-indigo-500/10 text-indigo-600 border-indigo-500/30",
  customer_service: "bg-pink-500/10 text-pink-600 border-pink-500/30",
  custom: "bg-gray-500/10 text-gray-600 border-gray-500/30",
  general: "bg-gray-500/10 text-gray-600 border-gray-500/30",
};

/* ───────────────────── Stat Card ───────────────────── */
function StatCard({ icon: Icon, label, value, color, subtext }) {
  return (
    <Card data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <CardContent className="p-4 flex items-center gap-4">
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className="text-2xl font-bold">{value}</p>
          {subtext && <p className="text-xs text-muted-foreground">{subtext}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

/* ───────────────────── Program Card ───────────────────── */
function ProgramCard({ program, onEdit, onDelete, onAddMaterial, onDeleteMaterial, onViewDetail, onEditQuiz, expanded, onToggle }) {
  const MaterialIcon = (type) => MATERIAL_ICONS[type] || FileText;

  return (
    <Card className="overflow-hidden" data-testid={`program-${program.id}`}>
      <div className="p-4">
        <div className="flex justify-between items-start gap-3">
          <div
            className="flex-1 cursor-pointer select-none"
            onClick={onToggle}
          >
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-lg">{program.title}</h3>
              <Badge variant="outline" className={CATEGORY_COLORS[program.category] || CATEGORY_COLORS.general}>
                {program.category}
              </Badge>
              <Badge variant="outline">{program.program_type}</Badge>
              {program.is_required && (
                <Badge className="bg-red-500/10 text-red-600 border-red-500/30">Required</Badge>
              )}
            </div>
            {program.description && (
              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{program.description}</p>
            )}
            <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {program.duration_minutes ? `${program.duration_minutes} min` : "No duration set"}
              </span>
              <span className="flex items-center gap-1">
                <Users className="h-3 w-3" />
                {program.target_audience}
              </span>
              <span className="flex items-center gap-1">
                <BookOpen className="h-3 w-3" />
                {program.materials_count} material{program.materials_count !== 1 ? "s" : ""}
              </span>
              <span className="flex items-center gap-1">
                <Send className="h-3 w-3" />
                {program.assignments_count} assigned
              </span>
              {program.passing_score && (
                <span className="flex items-center gap-1">
                  <Target className="h-3 w-3" />
                  Pass: {program.passing_score}%
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button size="icon" variant="ghost" onClick={onToggle} data-testid={`toggle-program-${program.id}`}>
              {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </Button>
            <Button size="icon" variant="ghost" onClick={() => onAddMaterial(program)} data-testid={`add-material-${program.id}`}>
              <Plus className="h-4 w-4" />
            </Button>
            <Button size="icon" variant="ghost" onClick={() => onEdit(program)}>
              <Pencil className="h-4 w-4" />
            </Button>
            <Button size="icon" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => onDelete(program.id)}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Expanded: Materials List */}
      {expanded && (
        <div className="border-t bg-muted/30">
          {(!program.materials || program.materials.length === 0) ? (
            <div className="p-6 text-center text-muted-foreground">
              <BookOpen className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No materials yet</p>
              <Button variant="link" size="sm" onClick={() => onAddMaterial(program)}>
                Add first material
              </Button>
            </div>
          ) : (
            <div className="divide-y">
              {program.materials.map((mat, idx) => {
                const Icon = MATERIAL_ICONS[mat.material_type] || FileText;
                return (
                  <div key={mat.id} className="flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors" data-testid={`material-${mat.id}`}>
                    <span className="text-xs text-muted-foreground w-6 text-center">{idx + 1}</span>
                    <div className="p-1.5 rounded bg-background border">
                      <Icon className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{mat.title}</p>
                      {mat.description && (
                        <p className="text-xs text-muted-foreground truncate">{mat.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0 text-xs text-muted-foreground">
                      {mat.duration_minutes && (
                        <span>{mat.duration_minutes} min</span>
                      )}
                      {mat.is_required && <Badge variant="outline" className="text-[10px] h-5">Required</Badge>}
                      {mat.external_url && (
                        <a href={mat.external_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      )}
                      {mat.material_type === "quiz" && (
                        <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={() => onEditQuiz && onEditQuiz(mat)} data-testid={`edit-quiz-${mat.id}`}>
                          <HelpCircle className="h-3 w-3 mr-1" />
                          Edit Quiz
                        </Button>
                      )}
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => onDeleteMaterial(mat.id)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

/* ───────────────────── Assignment Detail Dialog ───────────────────── */
function AssignmentDetailDialog({ open, onOpenChange, assignment, onMarkComplete, onTakeQuiz, onViewCertificate, onIssueCertificate }) {
  if (!assignment) return null;

  const completedCount = assignment.materials?.filter(m => m.progress?.completed_at).length || 0;
  const totalCount = assignment.materials?.length || 0;
  const pct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            {assignment.program_title}
          </DialogTitle>
          <DialogDescription>
            Assigned to {assignment.assignee_name} &middot; {assignment.assignee_type}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Progress Overview */}
          <div className="flex items-center gap-4">
            <Progress value={pct} className="flex-1" />
            <span className="font-semibold text-sm whitespace-nowrap">{pct}% complete</span>
          </div>

          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="bg-muted rounded-lg p-3">
              <p className="text-sm text-muted-foreground">Status</p>
              <p className="font-semibold capitalize">{assignment.status}</p>
            </div>
            <div className="bg-muted rounded-lg p-3">
              <p className="text-sm text-muted-foreground">Due Date</p>
              <p className="font-semibold">{assignment.due_date || "No deadline"}</p>
            </div>
            <div className="bg-muted rounded-lg p-3">
              <p className="text-sm text-muted-foreground">Score</p>
              <p className="font-semibold">{assignment.score ?? "N/A"}</p>
            </div>
          </div>

          {/* Certificate Button */}
          {assignment.status === "completed" && (
            <div className="flex justify-center">
              {assignment.certificate ? (
                <Button variant="outline" onClick={() => onViewCertificate(assignment.certificate)} data-testid="view-certificate-btn">
                  <Award className="h-4 w-4 mr-2" />
                  View Certificate
                </Button>
              ) : (
                <Button onClick={() => onIssueCertificate(assignment.id)} data-testid="issue-certificate-btn">
                  <Award className="h-4 w-4 mr-2" />
                  Issue Certificate
                </Button>
              )}
            </div>
          )}

          <Separator />

          {/* Materials Progress */}
          <div>
            <h4 className="font-medium mb-3">Materials Progress</h4>
            {(!assignment.materials || assignment.materials.length === 0) ? (
              <p className="text-sm text-muted-foreground">No materials in this program</p>
            ) : (
              <div className="space-y-2">
                {assignment.materials.map((mat) => {
                  const Icon = MATERIAL_ICONS[mat.material_type] || FileText;
                  const isComplete = !!mat.progress?.completed_at;
                  const isQuiz = mat.material_type === "quiz";
                  return (
                    <div key={mat.id} className={`flex items-center gap-3 p-3 rounded-lg border ${isComplete ? "bg-emerald-500/5 border-emerald-500/20" : "bg-background"}`}>
                      <div className={`p-1.5 rounded ${isComplete ? "bg-emerald-500/20 text-emerald-600" : "bg-muted text-muted-foreground"}`}>
                        {isComplete ? <CheckCircle2 className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${isComplete ? "line-through text-muted-foreground" : ""}`}>{mat.title}</p>
                        {mat.progress?.score != null && (
                          <p className="text-xs text-muted-foreground">
                            Score: {mat.progress.score}%
                            {mat.progress.attempts > 0 && ` | Attempts: ${mat.progress.attempts}`}
                          </p>
                        )}
                      </div>
                      {isQuiz ? (
                        <Button
                          size="sm"
                          variant={isComplete ? "outline" : "default"}
                          onClick={() => onTakeQuiz(assignment.id, mat.id, mat.title)}
                          data-testid={`take-quiz-${mat.id}`}
                        >
                          <HelpCircle className="h-3.5 w-3.5 mr-1" />
                          {isComplete ? "Retake" : "Take Quiz"}
                        </Button>
                      ) : !isComplete ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onMarkComplete(assignment.id, mat.id)}
                          data-testid={`complete-material-${mat.id}`}
                        >
                          <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                          Complete
                        </Button>
                      ) : (
                        <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30">Done</Badge>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {assignment.notes && (
            <>
              <Separator />
              <div>
                <h4 className="font-medium mb-1">Notes</h4>
                <p className="text-sm text-muted-foreground">{assignment.notes}</p>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ═══════════════════════════════════════════════════════
   MAIN TRAINING PAGE
   ═══════════════════════════════════════════════════════ */
export default function Training() {
  const [programs, setPrograms] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("programs");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  // Expanded program IDs
  const [expandedPrograms, setExpandedPrograms] = useState(new Set());

  // Dialog states
  const [showProgramDialog, setShowProgramDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showMaterialDialog, setShowMaterialDialog] = useState(false);
  const [showAssignmentDetail, setShowAssignmentDetail] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null); // program id
  // Phase 14: custom category manager dialog
  const [showCategoryManager, setShowCategoryManager] = useState(false);

  // Quiz & Certificate states
  const [quizBuilderMaterial, setQuizBuilderMaterial] = useState(null);
  const [quizTaker, setQuizTaker] = useState(null); // {assignmentId, materialId, materialTitle}
  const [certificate, setCertificate] = useState(null);

  // Form states
  const [programForm, setProgramForm] = useState({
    title: "", description: "", category: "general", program_type: "general",
    duration_minutes: "", passing_score: "", is_required: false, target_audience: "employees",
  });
  const [materialForm, setMaterialForm] = useState({
    title: "", description: "", material_type: "video", external_url: "",
    duration_minutes: "", sort_order: 0, is_required: true,
  });
  const [assignForm, setAssignForm] = useState({
    program_id: "", employee_ids: [], client_ids: [], due_date: "", notes: "", send_notification: true,
  });
  const [saving, setSaving] = useState(false);

  /* ───── Data Fetching ───── */
  const fetchData = useCallback(async () => {
    try {
      const [programsRes, assignmentsRes, statsRes, catsRes, empsRes, clientsRes] = await Promise.all([
        axios.get(`${API}/training/programs?active_only=false`),
        axios.get(`${API}/training/assignments`),
        axios.get(`${API}/training/stats`),
        axios.get(`${API}/training/categories`),
        axios.get(`${API}/employees`).catch(() => ({ data: [] })),
        axios.get(`${API}/clients`).catch(() => ({ data: [] })),
      ]);
      setPrograms(programsRes.data.programs || []);
      setAssignments(assignmentsRes.data.assignments || []);
      setStats(statsRes.data);
      setCategories(catsRes.data.categories || []);
      const empData = empsRes.data;
      setEmployees(Array.isArray(empData) ? empData : empData.employees || []);
      const cliData = clientsRes.data;
      setClients(Array.isArray(cliData) ? cliData : cliData.clients || []);
    } catch {
      toast.error("Failed to load training data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ───── Program with materials (for expanded view) ───── */
  const fetchProgramDetail = async (programId) => {
    try {
      const res = await axios.get(`${API}/training/programs/${programId}`);
      setPrograms(prev => prev.map(p => p.id === programId ? { ...p, materials: res.data.materials } : p));
    } catch {
      // silent fail
    }
  };

  const toggleProgram = (programId) => {
    setExpandedPrograms(prev => {
      const next = new Set(prev);
      if (next.has(programId)) {
        next.delete(programId);
      } else {
        next.add(programId);
        fetchProgramDetail(programId);
      }
      return next;
    });
  };

  /* ───── CRUD: Programs ───── */
  const handleSaveProgram = async () => {
    if (!programForm.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...programForm,
        duration_minutes: programForm.duration_minutes ? parseInt(programForm.duration_minutes) : null,
        passing_score: programForm.passing_score ? parseInt(programForm.passing_score) : null,
      };
      if (editingProgram) {
        await axios.put(`${API}/training/programs/${editingProgram.id}`, payload);
        toast.success("Program updated");
      } else {
        await axios.post(`${API}/training/programs`, payload);
        toast.success("Program created");
      }
      setShowProgramDialog(false);
      resetProgramForm();
      fetchData();
    } catch {
      toast.error("Failed to save program");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProgram = async (programId) => {
    try {
      await axios.delete(`${API}/training/programs/${programId}`);
      toast.success("Program deleted");
      setShowDeleteConfirm(null);
      fetchData();
    } catch {
      toast.error("Failed to delete program");
    }
  };

  /* ───── CRUD: Materials ───── */
  const handleAddMaterial = async () => {
    if (!selectedProgram || !materialForm.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...materialForm,
        duration_minutes: materialForm.duration_minutes ? parseInt(materialForm.duration_minutes) : null,
      };
      await axios.post(`${API}/training/programs/${selectedProgram.id}/materials`, payload);
      toast.success("Material added");
      setShowMaterialDialog(false);
      resetMaterialForm();
      fetchProgramDetail(selectedProgram.id);
      fetchData();
    } catch {
      toast.error("Failed to add material");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMaterial = async (materialId) => {
    try {
      await axios.delete(`${API}/training/materials/${materialId}`);
      toast.success("Material removed");
      // Refresh expanded programs
      expandedPrograms.forEach(pid => fetchProgramDetail(pid));
      fetchData();
    } catch {
      toast.error("Failed to delete material");
    }
  };

  /* ───── CRUD: Assignments ───── */
  const handleCreateAssignment = async () => {
    if (!assignForm.program_id) { toast.error("Select a program"); return; }
    if (assignForm.employee_ids.length === 0 && assignForm.client_ids.length === 0) {
      toast.error("Select at least one employee or client"); return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/training/assignments`, assignForm);
      toast.success(res.data.message || "Training assigned");
      setShowAssignDialog(false);
      resetAssignForm();
      fetchData();
    } catch {
      toast.error("Failed to assign training");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    try {
      await axios.delete(`${API}/training/assignments/${assignmentId}`);
      toast.success("Assignment removed");
      fetchData();
    } catch {
      toast.error("Failed to remove assignment");
    }
  };

  const handleViewAssignment = async (assignment) => {
    try {
      const res = await axios.get(`${API}/training/assignments/${assignment.id}`);
      setSelectedAssignment(res.data);
      setShowAssignmentDetail(true);
    } catch {
      toast.error("Failed to load assignment details");
    }
  };

  const handleMarkMaterialComplete = async (assignmentId, materialId) => {
    try {
      await axios.post(`${API}/training/assignments/${assignmentId}/progress/${materialId}`, { completed: true });
      toast.success("Material marked complete");
      // Refresh the detail
      const res = await axios.get(`${API}/training/assignments/${assignmentId}`);
      setSelectedAssignment(res.data);
      fetchData();
    } catch {
      toast.error("Failed to update progress");
    }
  };

  const handleTakeQuiz = (assignmentId, materialId, materialTitle) => {
    setQuizTaker({ assignmentId, materialId, materialTitle });
  };

  const handleQuizComplete = async () => {
    // Refresh assignment detail after quiz completion
    if (selectedAssignment) {
      const res = await axios.get(`${API}/training/assignments/${selectedAssignment.id}`);
      setSelectedAssignment(res.data);
    }
    fetchData();
  };

  const handleIssueCertificate = async (assignmentId) => {
    try {
      const res = await axios.post(`${API}/training/assignments/${assignmentId}/certificate`);
      setCertificate(res.data);
      toast.success("Certificate issued!");
      // Refresh assignment to show certificate
      const aRes = await axios.get(`${API}/training/assignments/${assignmentId}`);
      setSelectedAssignment(aRes.data);
    } catch {
      toast.error("Failed to issue certificate");
    }
  };

  const handleViewCertificate = (cert) => {
    setCertificate(cert);
  };

  /* ───── Form Resets ───── */
  const resetProgramForm = () => {
    setProgramForm({ title: "", description: "", category: "general", program_type: "general", duration_minutes: "", passing_score: "", is_required: false, target_audience: "employees" });
    setEditingProgram(null);
  };
  const resetMaterialForm = () => {
    setMaterialForm({ title: "", description: "", material_type: "video", external_url: "", duration_minutes: "", sort_order: 0, is_required: true });
  };
  const resetAssignForm = () => {
    setAssignForm({ program_id: "", employee_ids: [], client_ids: [], due_date: "", notes: "", send_notification: true });
  };

  const openEditProgram = (program) => {
    setEditingProgram(program);
    setProgramForm({
      title: program.title || "", description: program.description || "",
      category: program.category || "general", program_type: program.program_type || "general",
      duration_minutes: program.duration_minutes?.toString() || "", passing_score: program.passing_score?.toString() || "",
      is_required: program.is_required || false, target_audience: program.target_audience || "employees",
    });
    setShowProgramDialog(true);
  };

  const openAddMaterial = (program) => {
    setSelectedProgram(program);
    resetMaterialForm();
    setMaterialForm(prev => ({ ...prev, sort_order: (program.materials_count || 0) }));
    setShowMaterialDialog(true);
  };

  /* ───── Filtering ───── */
  const filteredPrograms = programs.filter(p => {
    const matchSearch = !searchTerm ||
      p.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchCat = categoryFilter === "all" || p.category === categoryFilter;
    return matchSearch && matchCat;
  });

  const filteredAssignments = assignments.filter(a => {
    const matchSearch = !searchTerm ||
      a.program_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.assignee_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = statusFilter === "all" || a.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const getStatusBadge = (status) => {
    const map = {
      completed: <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30">Completed</Badge>,
      in_progress: <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/30">In Progress</Badge>,
      overdue: <Badge className="bg-red-500/10 text-red-600 border-red-500/30">Overdue</Badge>,
      assigned: <Badge variant="outline">Assigned</Badge>,
    };
    return map[status] || <Badge variant="outline">{status}</Badge>;
  };

  const toggleAllEmployees = () => {
    if (assignForm.employee_ids.length === employees.length) {
      setAssignForm({ ...assignForm, employee_ids: [] });
    } else {
      setAssignForm({ ...assignForm, employee_ids: employees.map(e => e.id) });
    }
  };

  /* ───── Render ───── */
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in" data-testid="training-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-2">
            <GraduationCap className="h-8 w-8 text-primary" />
            Training Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Create programs, assign training, and track progress
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowAssignDialog(true)}
            data-testid="assign-training-btn"
          >
            <Send className="h-4 w-4 mr-2" />
            Assign Training
          </Button>
          <Button
            onClick={() => { resetProgramForm(); setShowProgramDialog(true); }}
            data-testid="create-program-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Program
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard icon={BookOpen} label="Programs" value={stats.total_programs} color="bg-primary/10 text-primary" />
          <StatCard icon={Send} label="Assigned" value={stats.total_assignments} color="bg-blue-500/10 text-blue-600" />
          <StatCard icon={CheckCircle2} label="Completed" value={stats.completed} color="bg-emerald-500/10 text-emerald-600" subtext={stats.completion_rate > 0 ? `${stats.completion_rate}% rate` : undefined} />
          <StatCard icon={TrendingUp} label="In Progress" value={stats.in_progress} color="bg-amber-500/10 text-amber-600" />
          <StatCard icon={AlertCircle} label="Overdue" value={stats.overdue} color="bg-red-500/10 text-red-600" />
        </div>
      )}

      {/* Search + Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search programs or assignments..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
            data-testid="training-search"
          />
        </div>
        {activeTab === "programs" && (
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-48" data-testid="category-filter">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {activeTab === "assignments" && (
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48" data-testid="status-filter">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="assigned">Assigned</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="programs" data-testid="programs-tab">
            <BookOpen className="h-4 w-4 mr-2" />
            Programs ({programs.length})
          </TabsTrigger>
          <TabsTrigger value="assignments" data-testid="assignments-tab">
            <Send className="h-4 w-4 mr-2" />
            Assignments ({assignments.length})
          </TabsTrigger>
        </TabsList>

        {/* ─── Programs Tab ─── */}
        <TabsContent value="programs" className="space-y-4 mt-4">
          {filteredPrograms.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <GraduationCap className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">
                  {searchTerm || categoryFilter !== "all" ? "No programs match your filters" : "No training programs yet"}
                </p>
                {!searchTerm && categoryFilter === "all" && (
                  <Button variant="link" onClick={() => { resetProgramForm(); setShowProgramDialog(true); }}>
                    Create your first program
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {filteredPrograms.map(program => (
                <ProgramCard
                  key={program.id}
                  program={program}
                  expanded={expandedPrograms.has(program.id)}
                  onToggle={() => toggleProgram(program.id)}
                  onEdit={openEditProgram}
                  onDelete={(id) => setShowDeleteConfirm(id)}
                  onAddMaterial={openAddMaterial}
                  onDeleteMaterial={handleDeleteMaterial}
                  onEditQuiz={(mat) => setQuizBuilderMaterial(mat)}
                  onViewDetail={() => {}}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* ─── Assignments Tab ─── */}
        <TabsContent value="assignments" className="mt-4">
          {filteredAssignments.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Send className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">
                  {searchTerm || statusFilter !== "all" ? "No assignments match your filters" : "No training assignments yet"}
                </p>
                {!searchTerm && statusFilter === "all" && (
                  <Button variant="link" onClick={() => setShowAssignDialog(true)}>
                    Assign training to get started
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Program</TableHead>
                      <TableHead>Assignee</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Due Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAssignments.map(assignment => (
                      <TableRow key={assignment.id} className="cursor-pointer hover:bg-muted/50" data-testid={`assignment-${assignment.id}`}>
                        <TableCell className="font-medium">{assignment.program_title}</TableCell>
                        <TableCell>{assignment.assignee_name || "Unknown"}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">{assignment.assignee_type || "N/A"}</Badge>
                        </TableCell>
                        <TableCell>{assignment.due_date || "-"}</TableCell>
                        <TableCell>{getStatusBadge(assignment.status)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button size="icon" variant="ghost" onClick={() => handleViewAssignment(assignment)} data-testid={`view-assignment-${assignment.id}`}>
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button size="icon" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => handleDeleteAssignment(assignment.id)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* ═══ DIALOGS ═══ */}

      {/* Create/Edit Program Dialog */}
      <Dialog open={showProgramDialog} onOpenChange={setShowProgramDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingProgram ? "Edit Program" : "Create Training Program"}</DialogTitle>
            <DialogDescription>
              {editingProgram ? "Update the program details below" : "Set up a new training program for your team"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={programForm.title}
                onChange={(e) => setProgramForm({ ...programForm, title: e.target.value })}
                placeholder="e.g., Safety Orientation"
                data-testid="program-title-input"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={programForm.description}
                onChange={(e) => setProgramForm({ ...programForm, description: e.target.value })}
                placeholder="Describe what this program covers..."
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select
                  value={programForm.category}
                  onValueChange={(v) => {
                    if (v === "__manage_custom__") {
                      setShowCategoryManager(true);
                    } else {
                      setProgramForm({ ...programForm, category: v });
                    }
                  }}
                >
                  <SelectTrigger data-testid="training-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.map(c => (
                      <SelectItem
                        key={c.id}
                        value={c.id}
                        data-testid={`training-category-opt-${c.id}`}
                        className={c.id === "__manage_custom__" ? "font-semibold text-primary border-t mt-1 pt-2" : ""}
                      >
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Type</Label>
                <Select value={programForm.program_type} onValueChange={(v) => setProgramForm({ ...programForm, program_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="certification">Certification</SelectItem>
                    <SelectItem value="mandatory">Mandatory</SelectItem>
                    <SelectItem value="optional">Optional</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Target Audience</Label>
                <Select value={programForm.target_audience} onValueChange={(v) => setProgramForm({ ...programForm, target_audience: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="employees">Employees</SelectItem>
                    <SelectItem value="clients">Clients</SelectItem>
                    <SelectItem value="both">Both</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Duration (minutes)</Label>
                <Input
                  type="number"
                  value={programForm.duration_minutes}
                  onChange={(e) => setProgramForm({ ...programForm, duration_minutes: e.target.value })}
                  placeholder="60"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Passing Score (%)</Label>
                <Input
                  type="number"
                  value={programForm.passing_score}
                  onChange={(e) => setProgramForm({ ...programForm, passing_score: e.target.value })}
                  placeholder="80"
                />
              </div>
              <div className="flex items-end pb-2">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={programForm.is_required}
                    onCheckedChange={(v) => setProgramForm({ ...programForm, is_required: v })}
                  />
                  <Label>Required for all</Label>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProgramDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveProgram} disabled={saving} data-testid="save-program-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingProgram ? "Update" : "Create"} Program
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Material Dialog */}
      <Dialog open={showMaterialDialog} onOpenChange={setShowMaterialDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Training Material</DialogTitle>
            <DialogDescription>
              Add content to "{selectedProgram?.title}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={materialForm.title}
                onChange={(e) => setMaterialForm({ ...materialForm, title: e.target.value })}
                placeholder="e.g., Introduction Video"
                data-testid="material-title-input"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={materialForm.description}
                onChange={(e) => setMaterialForm({ ...materialForm, description: e.target.value })}
                placeholder="Brief description of this material..."
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type</Label>
                <Select value={materialForm.material_type} onValueChange={(v) => setMaterialForm({ ...materialForm, material_type: v })}>
                  <SelectTrigger data-testid="material-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="video">Video</SelectItem>
                    <SelectItem value="document">Document</SelectItem>
                    <SelectItem value="link">External Link</SelectItem>
                    <SelectItem value="quiz">Quiz</SelectItem>
                    <SelectItem value="presentation">Presentation</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Duration (minutes)</Label>
                <Input
                  type="number"
                  value={materialForm.duration_minutes}
                  onChange={(e) => setMaterialForm({ ...materialForm, duration_minutes: e.target.value })}
                  placeholder="10"
                />
              </div>
            </div>
            <div>
              <Label>External URL</Label>
              <Input
                value={materialForm.external_url}
                onChange={(e) => setMaterialForm({ ...materialForm, external_url: e.target.value })}
                placeholder="https://youtube.com/watch?v=..."
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={materialForm.is_required}
                onCheckedChange={(v) => setMaterialForm({ ...materialForm, is_required: v })}
              />
              <Label>Required to complete program</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMaterialDialog(false)}>Cancel</Button>
            <Button onClick={handleAddMaterial} disabled={saving} data-testid="save-material-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Add Material
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Training Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Assign Training</DialogTitle>
            <DialogDescription>Select a program and choose who should complete it</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Training Program *</Label>
              <Select value={assignForm.program_id} onValueChange={(v) => setAssignForm({ ...assignForm, program_id: v })}>
                <SelectTrigger data-testid="assign-program-select"><SelectValue placeholder="Select a program" /></SelectTrigger>
                <SelectContent>
                  {programs.filter(p => p.is_active !== false).map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.title} ({p.category})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Due Date</Label>
              <Input
                type="date"
                value={assignForm.due_date}
                onChange={(e) => setAssignForm({ ...assignForm, due_date: e.target.value })}
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Employees</Label>
                {employees.length > 0 && (
                  <Button variant="link" size="sm" className="h-auto p-0" onClick={toggleAllEmployees} data-testid="select-all-employees">
                    {assignForm.employee_ids.length === employees.length ? "Deselect All" : "Select All"}
                  </Button>
                )}
              </div>
              <div className="border rounded-md p-3 max-h-40 overflow-y-auto space-y-2">
                {employees.map(emp => (
                  <div key={emp.id} className="flex items-center gap-2">
                    <Checkbox
                      checked={assignForm.employee_ids.includes(emp.id)}
                      onCheckedChange={(checked) => {
                        setAssignForm(prev => ({
                          ...prev,
                          employee_ids: checked
                            ? [...prev.employee_ids, emp.id]
                            : prev.employee_ids.filter(id => id !== emp.id)
                        }));
                      }}
                    />
                    <span className="text-sm">{emp.name}</span>
                  </div>
                ))}
                {employees.length === 0 && <p className="text-sm text-muted-foreground">No employees found</p>}
              </div>
            </div>
            <div>
              <Label>Clients</Label>
              <div className="border rounded-md p-3 max-h-40 overflow-y-auto space-y-2">
                {clients.map(client => (
                  <div key={client.id} className="flex items-center gap-2">
                    <Checkbox
                      checked={assignForm.client_ids.includes(client.id)}
                      onCheckedChange={(checked) => {
                        setAssignForm(prev => ({
                          ...prev,
                          client_ids: checked
                            ? [...prev.client_ids, client.id]
                            : prev.client_ids.filter(id => id !== client.id)
                        }));
                      }}
                    />
                    <span className="text-sm">{client.first_name} {client.last_name}</span>
                  </div>
                ))}
                {clients.length === 0 && <p className="text-sm text-muted-foreground">No clients found</p>}
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={assignForm.notes}
                onChange={(e) => setAssignForm({ ...assignForm, notes: e.target.value })}
                placeholder="Instructions or context for this assignment..."
                rows={2}
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={assignForm.send_notification}
                onCheckedChange={(v) => setAssignForm({ ...assignForm, send_notification: v })}
              />
              <Label>Send notification</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>Cancel</Button>
            <Button
              onClick={handleCreateAssignment}
              disabled={saving || !assignForm.program_id || (assignForm.employee_ids.length === 0 && assignForm.client_ids.length === 0)}
              data-testid="save-assignment-btn"
            >
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Assign Training
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Program</DialogTitle>
            <DialogDescription>
              This will permanently delete this training program, all its materials, and remove all assignments. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleDeleteProgram(showDeleteConfirm)} data-testid="confirm-delete-program">
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Program
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assignment Detail Dialog */}
      <AssignmentDetailDialog
        open={showAssignmentDetail}
        onOpenChange={setShowAssignmentDetail}
        assignment={selectedAssignment}
        onMarkComplete={handleMarkMaterialComplete}
        onTakeQuiz={handleTakeQuiz}
        onViewCertificate={handleViewCertificate}
        onIssueCertificate={handleIssueCertificate}
      />

      {/* Quiz Builder Dialog */}
      <QuizBuilder
        materialId={quizBuilderMaterial?.id}
        open={!!quizBuilderMaterial}
        onOpenChange={(open) => { if (!open) setQuizBuilderMaterial(null); }}
      />

      {/* Phase 14 — Custom Training Categories Manager */}
      <CategoryManager
        categoryType="training"
        label="Training Categories"
        controlledOpen={showCategoryManager}
        onControlledOpenChange={setShowCategoryManager}
        onCategoriesChange={() => {
          // refresh categories list when user adds/edits/removes
          axios.get(`${API}/training/categories`).then(r => setCategories(r.data.categories || []));
        }}
      />

      {/* Quiz Taker Dialog */}
      {quizTaker && (
        <QuizTaker
          assignmentId={quizTaker.assignmentId}
          materialId={quizTaker.materialId}
          materialTitle={quizTaker.materialTitle}
          open={!!quizTaker}
          onOpenChange={(open) => { if (!open) setQuizTaker(null); }}
          onComplete={handleQuizComplete}
        />
      )}

      {/* Certificate Viewer */}
      <CertificateViewer
        certificate={certificate}
        open={!!certificate}
        onOpenChange={(open) => { if (!open) setCertificate(null); }}
      />
    </div>
  );
}
